import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, setUnauthorizedHandler } from "@/lib/api";
import { identify, reset, track } from "@/lib/analytics";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem("neivai_user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(() => !!localStorage.getItem("neivai_token"));

  const persist = (token, u) => {
    if (token) localStorage.setItem("neivai_token", token);
    if (u) localStorage.setItem("neivai_user", JSON.stringify(u));
    setUser(u || null);
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    persist(data.token, data.user);
    identify(data.user.id, { email: data.user.email, business_name: data.user.business_name, role: data.user.role });
    track("login", { role: data.user.role });
    return data.user;
  };

  const signup = async (email, password, business_name) => {
    const { data } = await api.post("/auth/signup", { email, password, business_name });
    track("signup", { business_name });
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("neivai_token");
    localStorage.removeItem("neivai_user");
    setUser(null);
    reset();
  };

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      localStorage.setItem("neivai_user", JSON.stringify(data));
      setUser(data);
      identify(data.id, { email: data.email, business_name: data.business_name, role: data.role });
      return data;
    } catch (err) {
      const status = err?.response?.status;
      const detail = (err?.response?.data?.detail || "").toLowerCase();
      if (status === 401) {
        localStorage.removeItem("neivai_token");
        localStorage.removeItem("neivai_user");
        setUser(null);
        if (detail.includes("another device")) {
          toast.error("You have been logged out — account signed in on another device.");
        }
      }
      return null;
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("neivai_token");
    if (token) {
      refresh().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [refresh]);

  useEffect(() => {
    setUnauthorizedHandler((detail) => {
      const d = (detail || "").toLowerCase();
      localStorage.removeItem("neivai_token");
      localStorage.removeItem("neivai_user");
      setUser(null);
      if (d.includes("another device")) {
        toast.error("You have been logged out — account signed in on another device.");
      }
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, loading, login, signup, logout, refresh, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
